<script setup lang="ts">
import { RouterLink, useRouter } from 'vue-router';
import {
  FhButton,
  FhDropdownDivider,
  FhDropdownItem,
  FhDropdownMenu,
  FhDropdownSection,
  FhIcon,
  FhText,
} from '@fareharbor-com/beacon-vue';
import { bookmarks, search, user } from '@fareharbor-com/beacon-vue/icons';
import { storeToRefs } from 'pinia';
import { Logger } from '@/common/Logger';
import {
  API_DOCUMENTATION_URL,
  FEEDBACK_FORM_URL,
} from '@/common/constants/urls';
import { useTracking } from '@/composables/useTracking';
import { useUserRoles } from '@/composables/useUserRoles';
import { ROUTES } from '@/router/routes';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';

const tracking = useTracking();

const router = useRouter();

const authStore = useAuthStore();
const userStore = useUserStore();

const { isAuthenticated } = storeToRefs(authStore);
const { isFhdnApiAffiliateEnabled } = useUserRoles(userStore);

const doLogout = async () => {
  Logger.debug('[Component] Logout initiated');
  authStore.logout();
  await router.push({ name: ROUTES.LOGIN });
};

const openExternalLink = (url: string) => {
  window.open(url, '_blank');
};
</script>

<template>
  <nav class="bg-white border-b border-b-gray-300">
    <div class="flex flex-wrap justify-between items-center p-3 pr-12">
      <RouterLink
        class="flex items-center space-x-3 rtl:space-x-reverse py-2"
        :to="{ name: ROUTES.HOME }"
      >
        <img
          src="@/assets/images/logo.png"
          class="h-16 w-auto"
          alt="Logo"
          data-test-id="logo-icon"
        />
        <FhText
          tag="h1"
          variant="display-xl"
          weight="bold"
          color="primary-default"
          data-test-id="header-title-test"
        >
          Marketplace
        </FhText>
      </RouterLink>

      <div v-if="$route.name === ROUTES.ITEM_LIST" class="w-full sm:w-auto">
        <!-- Search bars moved to SearchBars.vue component -->
      </div>

      <div
        class="flex items-center space-x-3 sm:space-x-3 rtl:space-x-reverse mt-3 sm:mt-0"
        v-if="isAuthenticated"
      >
        <FhButton
          variant="tertiary"
          @click="router.push({ name: ROUTES.ITEM_LIST })"
          data-test-id="products-button"
        >
          <FhIcon :icon="search" />
          Products
        </FhButton>

        <FhButton
          variant="tertiary"
          @click="
            () => {
              router.push({
                name: ROUTES.ITEM_LIST,
                query: { isBookmarked: 'true' },
              });
              tracking?.trackClickBookmarksButton();
            }
          "
          data-test-id="item-bookmarks-button"
        >
          <FhIcon :icon="bookmarks" />
          Bookmarks
        </FhButton>

        <FhDropdownMenu class="mr-8">
          <template #default>
            <FhButton
              variant="tertiary"
              size="sm"
              data-test-id="account-dropdown-button"
            >
              <FhIcon :icon="user" />
              Settings
            </FhButton>
          </template>
          <template #sections>
            <FhDropdownSection>
              <RouterLink
                :to="{ name: ROUTES.PROFILE }"
                custom
                v-slot="{ navigate }"
              >
                <FhDropdownItem
                  @click="navigate"
                  data-test-id="profile-dropdown-item"
                >
                  Profile
                </FhDropdownItem>
              </RouterLink>
              <FhDropdownDivider></FhDropdownDivider>
              <FhDropdownItem
                @click="openExternalLink(FEEDBACK_FORM_URL)"
                data-test-id="feedback-dropdown-item"
              >
                Feedback
              </FhDropdownItem>

              <template v-if="!isFhdnApiAffiliateEnabled">
                <RouterLink
                  :to="{ name: ROUTES.FAQ }"
                  custom
                  v-slot="{ navigate }"
                >
                  <FhDropdownItem
                    @click="navigate"
                    data-test-id="help-dropdown-item"
                  >
                    Help Page
                  </FhDropdownItem>
                </RouterLink>
              </template>
              <template v-else>
                <FhDropdownItem
                  @click="openExternalLink(API_DOCUMENTATION_URL)"
                  data-test-id="api-docs-dropdown-item"
                >
                  API Documentation
                </FhDropdownItem>
              </template>
            </FhDropdownSection>
            <FhDropdownDivider></FhDropdownDivider>
            <FhDropdownSection>
              <FhDropdownItem
                @click="doLogout"
                data-test-id="logout-dropdown-item"
                id="logout-item"
              >
                Log out
              </FhDropdownItem>
            </FhDropdownSection>
          </template>
        </FhDropdownMenu>
      </div>
    </div>
  </nav>
</template>

<style scoped>
:deep(.fh-dropdown-item:not(:last-child)) {
  margin-bottom: 4px;
}
:deep(.fh-dropdown-divider) {
  margin-top: 8px;
  margin-bottom: 8px;
}
:deep(.fh-dropdown-divider:first-child) {
  margin-top: 0;
}
:deep(.fh-dropdown-divider:last-child) {
  margin-bottom: 0;
}
</style>
